// Typed clients for the /v1/pairs/* family. One function per backend
// route — kept dumb so call sites read like the URL they hit. Path
// params are URL-encoded; the list endpoint takes a `limit` because the
// backend currently has no cursor pagination (PR-1.5 scope).

import type { FirstFunding } from '../hooks/useFunding'
import { apiFetch } from './client'
import type {
  DexScreenerRefreshResponse,
  ListEnvelope,
  PairActivityItem,
  PairDetail,
  PairSummary,
  PairWindowEventsResponse,
  TradeSummary,
} from './types'

export interface PairRouteMode {
  phase?: 'launchpad'
  source?: string
  dex?: string
}

export interface PairResolvedRoute {
  pool_addr: string
}

export function fetchPairResolvedRoute(
  addr: string,
  opts?: RequestInit,
  mode?: PairRouteMode
): Promise<PairResolvedRoute> {
  return apiFetch(`/v1/pairs/${encodeURIComponent(addr)}/resolve${routeQuery(mode)}`, opts)
}

export function fetchPairs(limit = 50, opts?: RequestInit): Promise<ListEnvelope<PairSummary>> {
  return apiFetch(`/v1/pairs?limit=${limit}`, opts)
}

export function fetchPairDetail(
  addr: string,
  opts?: RequestInit,
  mode?: PairRouteMode
): Promise<PairDetail> {
  return apiFetch<PairDetail>(
    `/v1/pairs/${encodeURIComponent(addr)}${routeQuery(mode, { surface_wire: '5' })}`,
    opts
  ).then(expandPairWindowEvents)
}

interface PairWindowEventsWire {
  as_of: string
  pool_addr: string
  base_token: string
  launchpad_source?: string
  pools: string[]
  cursor_ts: number
  cursor_lt: number
  window_events_compact_v4: PairDetail['window_events_compact_v4']
}

export function fetchPairWindowEvents(
  addr: string,
  detail: PairDetail,
  opts?: RequestInit,
  mode?: PairRouteMode
): Promise<PairWindowEventsResponse> {
  if (!detail.stats_as_of) {
    return Promise.reject(new Error('pair seed has no exact stats_as_of boundary'))
  }
  return apiFetch<PairWindowEventsWire>(
    `/v1/pairs/${encodeURIComponent(addr)}/window-events${routeQuery(mode, {
      as_of: detail.stats_as_of,
      seed_pool: detail.pool_addr,
      seed_base: detail.base_token,
      seed_launchpad: detail.launchpad_source ?? '',
    })}`,
    opts
  ).then((wire) => {
    if (
      wire.pool_addr !== detail.pool_addr ||
      wire.base_token !== detail.base_token ||
      (wire.launchpad_source ?? '') !== (detail.launchpad_source ?? '')
    ) {
      throw new Error('pair window tape route does not match the committed seed')
    }
    const expanded = expandPairWindowEvents({
      ...detail,
      window_events_compact_v4: wire.window_events_compact_v4,
    })
    const events = expanded.window_events ?? []
    const last = events.at(-1)
    if (
      (last && (last.ts !== wire.cursor_ts || (last.lt ?? 0) !== wire.cursor_lt)) ||
      (!last && (wire.cursor_ts !== 0 || wire.cursor_lt !== 0))
    ) {
      throw new Error('pair window tape cursor does not cover the decoded projection')
    }
    return {
      as_of: wire.as_of,
      pool_addr: wire.pool_addr,
      base_token: wire.base_token,
      launchpad_source: wire.launchpad_source,
      pools: wire.pools,
      cursor_ts: wire.cursor_ts,
      cursor_lt: wire.cursor_lt,
      window_events: events,
    }
  })
}

export function fetchPairTrades(
  addr: string,
  limit = 50,
  opts?: RequestInit,
  mode?: PairRouteMode,
  order: 'latest' | 'oldest' = 'latest'
): Promise<ListEnvelope<TradeSummary>> {
  const extra: Record<string, string> = { limit: String(limit) }
  if (order === 'oldest') extra.order = order
  const q = routeQuery(mode, extra)
  return apiFetch(`/v1/pairs/${encodeURIComponent(addr)}/trades${q}`, opts)
}

export function fetchPairActivity(
  addr: string,
  limit = 50,
  opts?: RequestInit,
  mode?: PairRouteMode,
  scope?: 'liquidity',
  query?: {
    order?: 'latest' | 'oldest'
    filter?: 'dev' | 'wallet' | 'kols' | 'tracking'
    providerAddr?: string
    cursor?: PairActivityItem
  }
): Promise<ListEnvelope<PairActivityItem>> {
  const extra: Record<string, string> = { limit: String(limit) }
  if (scope) extra.scope = scope
  if (query?.order === 'oldest') extra.order = 'oldest'
  if (query?.filter) extra.filter = query.filter
  if (query?.providerAddr) extra.provider_addr = query.providerAddr
  if (query?.cursor) {
    extra.cursor_ts = String(query.cursor.ts)
    extra.cursor_lt = String(query.cursor.lt ?? 0)
    extra.cursor_ordinal = String(query.cursor.action_ordinal)
    extra.cursor_hash = query.cursor.tx_hash
    extra.cursor_type = query.cursor.activity_type
  }
  const q = routeQuery(mode, extra)
  return apiFetch(`/v1/pairs/${encodeURIComponent(addr)}/activity${q}`, opts)
}

export interface PairSeedResponse {
  pair: PairDetail
  trades: ListEnvelope<TradeSummary>
  activity: ListEnvelope<PairActivityItem>
}

// Composed pair-page seed: ONE request for the detail+trades+activity
// envelope. The backend builds every section through the same code as the
// standalone endpoints, so the bodies are identical — the trade rows still
// come from the trades section, hence activity stays on the liquidity scope.
// ref resolution (pool or token master) happens server-side in the same
// lifecycle lookup the per-section endpoints use.
export function fetchPairSeed(
  addr: string,
  limits: { trades: number; activity: number },
  opts?: RequestInit,
  mode?: PairRouteMode
): Promise<PairSeedResponse> {
  const q = routeQuery(mode, {
    ref: addr,
    trades_limit: String(limits.trades),
    activity_limit: String(limits.activity),
    surface_wire: '5',
    scope: 'liquidity',
  })
  return apiFetch<PairSeedResponse>(`/v1/pairs/seed${q}`, opts).then((seed) => ({
    ...seed,
    pair: expandPairWindowEvents(seed.pair),
  }))
}

// /v1/tokens/native — synthetic TON-as-a-token detail body. Same
// wire shape as PairDetail; backend reuses the type.
export function fetchNativeDetail(opts?: RequestInit): Promise<PairDetail> {
  return apiFetch('/v1/tokens/native', opts)
}

export function fetchNativeTrades(
  limit = 50,
  opts?: RequestInit
): Promise<ListEnvelope<TradeSummary>> {
  return apiFetch(`/v1/tokens/native/trades?limit=${limit}`, opts)
}

// 5-minute aggregate window — fuels the top-right stats widget on
// /tokens/native (buy/sell vol, txns, trader cardinalities).
export interface NativeStats5m {
  buy_volume: number
  sell_volume: number
  buy_txns: number
  sell_txns: number
  traders: number
  buyers: number
  sellers: number
  window_sec: number
}

export function fetchNativeStats(opts?: RequestInit): Promise<NativeStats5m> {
  return apiFetch('/v1/tokens/native/stats', opts)
}

export function refreshDexScreenerProfile(
  master: string,
  opts?: RequestInit
): Promise<DexScreenerRefreshResponse> {
  return apiFetch(`/v1/tokens/${encodeURIComponent(master)}/dexscreener/refresh`, {
    ...opts,
    method: 'POST',
  })
}

function routeQuery(mode?: PairRouteMode, extra?: Record<string, string>): string {
  const params = new URLSearchParams(extra)
  if (mode?.phase) params.set('phase', mode.phase)
  if (mode?.source) params.set('source', mode.source)
  if (mode?.dex) params.set('dex', mode.dex)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

function expandPairWindowEvents(detail: PairDetail): PairDetail {
  const compactV4 = detail.window_events_compact_v4
  if (compactV4) {
    const {
      window_events_compact_v2: _v2,
      window_events_compact_v3: _v3,
      window_events_compact_v4: _v4,
      ...surface
    } = detail
    const [baseTS, baseLT, tsDeltas, ltDeltas, pools, sides, quote, native, rates, refs, direct] =
      compactV4
    let ts = baseTS
    let lt = baseLT
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: one positional decoder owns the versioned wire contract
    const windowEvents = tsDeltas.map((tsDelta, index) => {
      ts = index === 0 ? baseTS + tsDelta : ts + tsDelta
      lt = index === 0 ? baseLT + (ltDeltas[index] ?? 0) : lt + (ltDeltas[index] ?? 0)
      const pool = pools?.[index] ?? 0
      const nativePrice = native[index] ?? 0
      const ref = refs[index] ?? -1
      const usdPrice =
        ref === -1
          ? null
          : ref >= 0
            ? nativePrice * (rates[ref] ?? Number.NaN)
            : (direct[-2 - ref] ?? null)
      const quoteNative = quote[index] ?? 0
      return {
        ts,
        lt,
        tx_hash: '',
        pool_addr: pool === 0 ? detail.pool_addr : pool === 1 ? detail.base_token : pool,
        base_token: detail.base_token,
        side: sides[index] === 'b' ? 'buy' : sides[index] === 's' ? 'sell' : 'unknown',
        quote_native: quoteNative,
        quote_usd: pairEventQuoteUSD(quoteNative, nativePrice, usdPrice),
        price: nativePrice,
        native_price: nativePrice,
        usd_price: Number.isFinite(usdPrice) ? usdPrice : null,
      }
    })
    return {
      ...surface,
      window_events: windowEvents,
    }
  }
  const compactV3 = detail.window_events_compact_v3
  if (compactV3) {
    const {
      window_events_compact_v2: _v2,
      window_events_compact_v3: _v3,
      window_events_compact_v4: _v4,
      ...surface
    } = detail
    return {
      ...surface,
      window_events: compactV3.map(([ts, lt, pool, side, quoteNative, nativePrice, usdPrice]) => ({
        ts,
        lt,
        tx_hash: '',
        pool_addr: pool === 0 ? detail.pool_addr : pool === 1 ? detail.base_token : pool,
        base_token: detail.base_token,
        side: side === 1 ? 'buy' : side === -1 ? 'sell' : 'unknown',
        quote_native: quoteNative,
        quote_usd: pairEventQuoteUSD(quoteNative, nativePrice, usdPrice),
        price: nativePrice,
        native_price: nativePrice,
        usd_price: usdPrice,
      })),
    }
  }
  const compact = detail.window_events_compact_v2
  if (!compact) return detail
  const {
    window_events_compact_v2: _compact,
    window_events_compact_v3: _v3,
    window_events_compact_v4: _v4,
    ...surface
  } = detail
  return {
    ...surface,
    window_events: compact.map(
      ([ts, lt, txHash, pool, side, quoteNative, quoteUSD, nativePrice, usdPrice]) => ({
        ts,
        lt,
        tx_hash: txHash,
        pool_addr: pool === 0 ? detail.pool_addr : pool === 1 ? detail.base_token : pool,
        base_token: detail.base_token,
        side: side === 1 ? 'buy' : side === -1 ? 'sell' : 'unknown',
        quote_native: quoteNative,
        quote_usd: quoteUSD,
        price: nativePrice,
        native_price: nativePrice,
        usd_price: usdPrice,
      })
    ),
  }
}

function pairEventQuoteUSD(
  quoteNative: number,
  nativePrice: number,
  usdPrice: number | null
): number | null {
  if (!(nativePrice > 0) || usdPrice == null || !Number.isFinite(usdPrice)) return null
  const quoteUSD = quoteNative * (usdPrice / nativePrice)
  return Number.isFinite(quoteUSD) && quoteUSD >= 0 ? quoteUSD : null
}

// Top traders for one pool. TON amounts stay exact decimal strings (raw
// nanoTON on the wire); USD figures are display-grade numbers computed from
// each trade's own valuation. PnL is an average-cost walk over the pool's
// indexed trades — `pnl_partial` marks a wallet whose sells exceed everything
// we saw it buy (tokens arrived via transfers we do not index).
//
// The positions reader (plan GLOBAL-WALLET-PORTFOLIO-20261025 §4, lane L4)
// adds the optional fields after `funding`: a status and its reasons, the
// proceeds of sales without a known cost, and lifetime figures for the pool.
// A response without them is today's read and renders as today.
export type TopTrader = {
  trader_addr: string
  trades: number
  buys: number
  sells: number
  bought_ton: string
  sold_ton: string
  volume_ton: string
  net_ton: string
  bought_usd?: number
  sold_usd?: number
  volume_usd?: number
  bought_base?: number
  sold_base?: number
  realized_pnl_usd?: number
  unrealized_pnl_usd?: number
  total_pnl_usd?: number
  balance_base?: number
  balance_usd?: number
  win_rate_pct?: number
  avg_buy_mcap_usd?: number
  /** Average market cap of this wallet's sells (asked of the backend
      2026-10-26, not shipped yet); absent = not read. */
  avg_sell_mcap_usd?: number | null
  last_active_ts?: number
  /** First observed buy in the cost-basis walk — holding duration, not last active. */
  first_buy_ts?: number
  pnl_partial?: boolean
  funding?: FirstFunding | null
  /** L4: decides over `pnl_partial` when present. Top traders is never `complete`. */
  pnl_status?: PnLStatus
  pnl_reasons?: PnLReason[]
  proceeds_without_basis_usd?: number | null
  /** L4: trades in the window with no dollar price */
  unpriced_trades?: number | null
  /** L4: lifetime in this pool, whatever the window. */
  lifetime_realized_pnl_usd?: number | null
  lifetime_bought_usd?: number | null
  lifetime_sold_usd?: number | null
  lifetime_trades?: number | null
  lifetime_win_rate_pct?: number | null
  /** unix seconds of the price unrealized uses; an older reader sent RFC3339
      (lib/pnlStatus parseMarkTs reads both) */
  mark_ts?: number | string | null
  mark_stale?: boolean
  // ---- the wallet's native balance (PR #1123, 2026-10-26: our node's read;
  // a failed / saturated read sends neither field) — absent = not read,
  // never zero ----
  /** GRAM in nano, a decimal string ("0" for a proven non-existent account) */
  gram_balance_nano?: string | null
  /** unix seconds of that balance read */
  gram_balance_as_of?: number | null
  // ---- journal mode (wiki/pr/JOURNAL-REALTIME-PUSH-20261102.md): the
  // balance's row in our journal and its version, the order key of the
  // `holders.journal` frames on holders:<token>; absent = trade walk ----
  quantity_version?: number | null
  /** the token balance in raw units, a decimal string */
  balance_raw?: string | null
  quantity_source?: QuantitySource
}

/** Whose figures a Top traders column holds (L4 `column_scope`); total PnL
    is "pool+token_position" — window realized (pool) + unrealized (token). */
export type ColumnScope = 'pool' | 'token_position' | 'pool+token_position'

export type TopTradersResponse = {
  pool_addr: string
  window_days: number
  basis_window_days?: number
  items?: TopTrader[]
  quote_asset: string
  sort?: TopTradersSort
  direction?: 'asc' | 'desc'
  ranking_limit?: number
  scan_cap?: number
  scan_truncated?: boolean
  scope?: 'indexed_pool_trades'
  amounts_note?: string
  /** L4: "positions" when the positions reader answered; absent = the older
      walk (a pool not folded yet), which sends none of the L4 fields. */
  source?: 'positions' | (string & {})
  /** The token (positions read); a pool equal to it is the bonding curve. */
  token?: string | null
  /** The pool whose last priced trade is the token's mark (positions read). */
  mark_pool?: string | null
  mark_usd?: number | null
  /** L4: activity and realized are this pool's; balance and unrealized the token position. */
  column_scope?: {
    activity?: ColumnScope
    realized?: ColumnScope
    balance?: ColumnScope
    unrealized?: ColumnScope
    total?: ColumnScope
  }
}

export type TopTradersSort =
  | 'pnl'
  | 'realized'
  | 'unrealized'
  | 'bought'
  | 'sold'
  | 'volume'
  | 'remaining'
  | 'active'

/**
 * A position's PnL status (plan §3: positions.BasisStatus plus the readers'
 * per-row degrades). The escape keeps an unknown future code a string, which
 * renders with no marker rather than failing.
 */
export type PnLStatus =
  | 'complete'
  | 'partial'
  | 'pending'
  | 'unknown'
  | 'no_trades'
  | 'quote_asset'
  | 'unavailable'
  | (string & {})

/** One entry of pnl_reasons[]: the vocabulary of backend/pkg/positions/types.go. */
export type PnLReason =
  | 'pre_coverage'
  | 'unobserved_acquisition'
  | 'transfer_in'
  | 'unpriced_trade'
  | 'token_fee_unknown'
  | 'decimals_unknown'
  | 'raw_inexact'
  | 'quantity_deficit'
  | 'quantity_unverified'
  | 'quantity_journal'
  | 'transfer_out'
  | 'moves_unchecked'
  // read-time reasons of the readers (backend/pkg/api/pairs/positions_read.go)
  | 'projector_not_final'
  | 'fold_queued'
  | 'projector_stale'
  | 'projector_unpublished'
  | 'positions_unavailable'
  | 'no_indexed_trades'
  | 'chain_unavailable'
  | 'mark_unavailable'
  | 'observation_untimed'
  | 'token_decimals_unknown'
  // no unit ever acquired at a known price: PnL figures null, status unknown
  | 'basis_unknown'
  // Holders: the balance moved after (or 2 min before) the certified tape → pending
  | 'balance_changed_after_tape'
  // informational: the certified tape is more than 40 min behind
  | 'tape_certified_behind'
  // Top traders: the quote asset's decimals are unknown (quote amounts withheld)
  | 'quote_decimals_unknown'
  | (string & {})

/** Where a shown balance came from (L3 QuantityResolver). */
export type QuantitySource = 'chain' | 'journal' | 'trades' | (string & {})

export type HolderItem = {
  owner: string
  amount_raw: string
  pct?: number | null
  sniper: boolean
  bundler: boolean
  insider: boolean
  phishing: boolean
  dev: boolean
  /** unix seconds from jetton_ops.ts, not ingest updated_at */
  last_active_ts?: number
  bought_usd?: number | null
  sold_usd?: number | null
  bought_base?: number | null
  sold_base?: number | null
  avg_buy_mcap_usd?: number | null
  /** Average market cap of this wallet's sells (asked of the backend
      2026-10-26, not shipped yet); absent = not read. */
  avg_sell_mcap_usd?: number | null
  realized_pnl_usd?: number | null
  unrealized_pnl_usd?: number | null
  funding?: FirstFunding | null
  // ---- L3 positions reader (plan §3); absent on today's read ----
  pnl_status?: PnLStatus
  pnl_reasons?: PnLReason[]
  unknown_basis_raw?: string | null
  proceeds_without_basis_usd?: number | null
  quantity_source?: QuantitySource
  /** The journal's figure, sent only when it differs from the chain amount shown. */
  journal_amount_raw?: string | null
  coverage_from?: string | null
  /** unix seconds of the price unrealized uses (RFC3339 from an older reader) */
  mark_ts?: number | string | null
  mark_stale?: boolean
  // ---- the wallet's trades of this token and its native balance (PR #1123,
  // 2026-10-26). A count of 0 only when the token's whole life is proven on
  // the tape, else absent (pnl_reasons then holds history_unproven);
  // first_buy_ts only with a buy; GRAM as on TopTrader. Absent = not read,
  // never zero ----
  trades?: number | null
  buys?: number | null
  sells?: number | null
  /** unix seconds of the first indexed buy (how long it has held) */
  first_buy_ts?: number | null
  /** GRAM in nano, a decimal string ("0" for a proven non-existent account) */
  gram_balance_nano?: string | null
  /** unix seconds of that balance read */
  gram_balance_as_of?: number | null
  /** journal mode: the version of this owner's journal row (0 is a real
      version), the order key of the `holders.journal` frames */
  quantity_version?: number | null
}

export type TokenHoldersResponse = {
  jetton_master: string
  as_of: string
  t_open?: string | null
  holders: number
  top10_pct?: number | null
  dev_pct?: number | null
  snipers_pct?: number | null
  insiders_pct?: number | null
  bundlers_pct?: number | null
  phishing_pct?: number | null
  pro_traders_pct?: number | null
  supply_raw?: string
  decimals: number
  note?: string
  trades_note?: string
  /** L3: holder count, Top 10 and the row order still come from the journal. */
  census_source?: 'journal' | (string & {})
  /** "journal": the balances are our journal's rows, pushed live on
      holders:<token> (wiki/pr/JOURNAL-REALTIME-PUSH-20261102.md) */
  holders_source?: 'journal' | (string & {})
  /** journal mode: owners the census leaves out (the zero address, the
      token's pools, curves quoted in it) — a frame for one is ignored */
  excluded_owners?: string[] | null
  /** The pool whose last priced trade is the token's mark (positions read). */
  mark_pool?: string | null
  mark_usd?: number | null
  items?: HolderItem[]
}

const holdersSnapshots = new Map<string, { data: TokenHoldersResponse; at: number }>()
const pendingHolders = new Map<string, Promise<TokenHoldersResponse | null>>()

export async function fetchTokenHolders(
  addr: string,
  opts?: RequestInit,
  mode?: PairRouteMode
): Promise<TokenHoldersResponse | null> {
  const path = `/v1/pairs/${encodeURIComponent(addr)}/holders${routeQuery(mode, { limit: '50' })}`
  // Rail and Holders request the same page. Share that snapshot for five seconds;
  // explicit request options (including cancellation) keep independent semantics.
  if (!opts) {
    const cached = holdersSnapshots.get(path)
    if (cached && Date.now() - cached.at < 5_000) return cached.data
    const pending = pendingHolders.get(path)
    if (pending) return pending
  }
  const request = apiFetch<TokenHoldersResponse>(path, opts)
    .then((data) => {
      if (!opts) {
        if (holdersSnapshots.size >= 32) {
          holdersSnapshots.delete(holdersSnapshots.keys().next().value as string)
        }
        holdersSnapshots.set(path, { data, at: Date.now() })
      }
      return data
    })
    .catch(() => null)
    .finally(() => {
      if (!opts) pendingHolders.delete(path)
    })
  if (!opts) pendingHolders.set(path, request)
  return request
}

export async function fetchTopTraders(
  poolAddr: string,
  windowDays = 7,
  limit = 25,
  sort: TopTradersSort = 'pnl',
  direction: 'asc' | 'desc' = 'desc'
): Promise<TopTradersResponse | null> {
  try {
    return await apiFetch<TopTradersResponse>(
      `/v1/pairs/${encodeURIComponent(poolAddr)}/top-traders?window_days=${windowDays}&limit=${limit}&sort=${sort}&direction=${direction}`
    )
  } catch {
    return null
  }
}
